import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { directionToDegree, safeNum } from './dot-cms-aniamtion.helper';
import { AnimationType,AnimationDynamicSettings } from './animation/animations.model';

gsap.registerPlugin(ScrollTrigger);

export const scrollAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween>
> = {
  'scroll-spin': (s, el, opts) => {
    const spinCount = safeNum(s.spin_count, 1);
    const scaleVal = safeNum(s.scale, 1);
    const isCCW = /counter|anti|ccw/i.test(String(s.direction || ''));
    const rotDir = isCCW ? -360 : 360;

    return gsap.to(el, {
      rotation: rotDir * spinCount,
      scale: scaleVal,
      ...opts, // includes safe duration, delay, ease, repeat, yoyo

      scrollTrigger: {
        trigger: el,
        start: s.start ?? 'top 70%', // CMS or fallback
        end: s.end ?? 'bottom 20%',
        scrub: s.scrub ?? true, // CMS or default true
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false, // markers only if enabled
      },
    });
  },

  'scroll-move': (s, el, opts) => {
    // Read DotCMS values with fallbacks
    const angle = safeNum(s.angle, 0); // degrees
    const distance = safeNum(s.distance, 100); // px

    // Convert angle to radians
    const rad = (angle * Math.PI) / 180;

    // Compute displacement
    const x = Math.cos(rad) * distance;
    const y = Math.sin(rad) * distance;

    return gsap.to(el, {
      x,
      y,
      ...opts, // duration, delay, ease, repeat, yoyo etc.

      scrollTrigger: {
        trigger: el,
        start: s.start ?? 'top 80%', // fallback like spin
        end: s.end ?? 'bottom 20%',
        scrub: s.scrub ?? true,
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-expand': (s, el, opts) => {
    const scaleVal = safeNum(s.scale, 1.2);
    const speed = safeNum(s.speed, 1); // controls scrub speed
    // const area = safeNum(s.animationArea, 100); // viewport area % for effect

    // Optional: use direction to expand more strongly along x/y
    let transform: gsap.TweenVars = { scale: scaleVal };
    if (s.direction) {
      const dir = String(s.direction).toLowerCase();
      if (dir === 'up') transform = { scaleY: scaleVal };
      else if (dir === 'down') transform = { scaleY: scaleVal };
      else if (dir === 'left') transform = { scaleX: scaleVal };
      else if (dir === 'right') transform = { scaleX: scaleVal };
    }

    return gsap.to(el, {
      ...transform,
      ...opts, // includes duration, delay, ease, repeat, yoyo

      scrollTrigger: {
        trigger: el,
        start: s.start ?? 'top 80%',
        end: s.end ?? 'bottom 20%',
        scrub: s.scrub ?? speed, // scrub speed from CMS
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-shrink': (s, el, opts) => {
    const scaleVal = safeNum(s.scale, 0.8); // shrink factor (default 0.8)
    const speed = safeNum(s.speed, 1); // scrub speed
    // const area = safeNum(s.animationArea, 100); // scroll area length

    // Direction handling: shrink along axis if specified
    let transform: gsap.TweenVars = { scale: scaleVal };
    if (s.direction) {
      const dir = String(s.direction).toLowerCase();
      if (dir === 'up' || dir === 'down') {
        transform = { scaleY: scaleVal };
      } else if (dir === 'left' || dir === 'right') {
        transform = { scaleX: scaleVal };
      }
    }

    return gsap.to(el, {
      ...transform,
      ...opts, // includes delay, duration, ease, repeat, yoyo

      scrollTrigger: {
        trigger: el,
        start: s.start ?? 'top 80%',
        scrub: s.scrub ?? speed,
        end: s.end ?? 'bottom 20%',
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-fade': (s, el, opts) => {
    const startOpacity = safeNum(s.startOpacity, 0);
    // const area = safeNum(s.animationArea, 100);

    return gsap.fromTo(
      el,
      { opacity: startOpacity },
      {
        opacity: 1,
        ...opts, // includes delay, duration, ease, repeat, yoyo

        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-slide': (s, el, opts) => {
    const area = safeNum(s.animationArea, 100);
    const rad = directionToDegree(s.direction ?? 'up');

    // offset based on direction + area
    const dx = Math.cos(rad) * area;
    const dy = Math.sin(rad) * area;

    return gsap.fromTo(
      el,
      { x: dx, y: dy, opacity: s.startOpacity ?? 0 },
      {
        x: 0,
        y: 0,
        opacity: 1,
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-blur': (s, el, opts) => {
    // const area = safeNum(s.animationArea, 100);
    const blur = safeNum(s.blur_intensity, 10);

    return gsap.fromTo(
      el,
      { filter: `blur(${blur}px)` },
      {
        filter: 'blur(0px)',
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-reveal': (s, el, opts) => {
    const area = safeNum(s.animationArea, 100); // how far to offset
    const dir = String(s.direction ?? 'up').toLowerCase();
    const rad = directionToDegree(dir); // reuse your helper

    // offset based on direction
    const dx = Math.cos(rad) * area;
    const dy = Math.sin(rad) * area;

    return gsap.fromTo(
      el,
      {
        x: dx,
        y: dy,
        opacity: s.startOpacity ?? 0, // hidden & offset
      },
      {
        x: 0,
        y: 0,
        opacity: 1,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-shape': (s, el, opts) => {
    const shape = String(s.shape ?? 'square').toLowerCase();
    const intensity = safeNum(s.intensity, 1);
    const area = safeNum(s.animationArea, 100);

    // Map DotCMS shapes → clip-path definitions
    const shapeMap: Record<string, string> = {
      square: 'inset(0% 0% 0% 0%)',
      circle: `circle(${intensity * 0}% at 50% 50%)`,
      oval: `ellipse(${intensity * 0}% ${intensity * 0}% at 50% 50%)`,
      diamond: `polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)`,
    };

    const startShape = shapeMap[shape] ?? shapeMap['square'];
    let endShape: string;

    if (shape === 'circle') {
      endShape = `circle(${intensity * area}% at 50% 50%)`;
    } else if (shape === 'oval') {
      endShape = `ellipse(${intensity * area}% ${intensity * (area / 2)}% at 50% 50%)`;
    } else if (shape === 'diamond') {
      // diamond just scales from tiny → full
      endShape = `polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)`;
    } else {
      endShape = 'inset(0% 0% 0% 0%)';
    }

    // Reset before animating
    gsap.set(el, {
      clipPath: startShape,
      webkitClipPath: startShape,
      opacity: s.startOpacity ?? 0,
    });

    return gsap.to(el, {
      clipPath: endShape,
      webkitClipPath: endShape,
      opacity: 1,
      ...opts,

      scrollTrigger: {
        trigger: el,
        start: s.start ?? 'top 80%',
        end: s.end ?? 'bottom 20%',
        scrub: s.scrub ?? true,
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-arc': (s, el, opts) => {
    const area = safeNum(s.animationArea, 100);
    const dir = String(s.direction || 'up').toLowerCase();

    // Arc path calculation: use sine/cosine to simulate a curve
    // We'll make the element follow a quarter-circle arc
    const arcRadius = area;
    let dx = 0,
      dy = 0;

    if (dir === 'up') {
      dx = arcRadius; // move right
      dy = -arcRadius; // move up
    } else if (dir === 'down') {
      dx = arcRadius; // move right
      dy = arcRadius; // move down
    } else if (dir === 'left') {
      dx = -arcRadius; // move left
      dy = -arcRadius; // move up
    } else if (dir === 'right') {
      dx = arcRadius; // move right
      dy = arcRadius; // move down
    }

    // Use a motionPath-like bezier for arc effect
    return gsap.fromTo(
      el,
      { x: 0, y: 0, opacity: s.startOpacity ?? 0 },
      {
        motionPath: {
          path: [
            { x: 0, y: 0 },
            { x: dx / 2, y: 0 }, // mid control point
            { x: dx, y: dy }, // end point
          ],
          curviness: 1.5,
        },
        opacity: 1,
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-3dspin': (s, el, opts) => {
    const rotation = safeNum(s.rotation, 360); // degrees of spin
    const speed = safeNum(s.speed, 1); // multiplier for scrub intensity
    const area = safeNum(s.animationArea, 100);

    return gsap.fromTo(
      el,
      {
        rotationX: 0,
        rotationY: 0,
        z: 0,
        opacity: s.startOpacity ?? 1,
      },
      {
        rotationX: rotation,
        rotationY: rotation,
        z: area, // push out/in depth
        duration: speed,
        transformPerspective: 800, // adds 3D depth
        transformOrigin: 'center center',
        ease: s.easing ?? 'power1.inOut',
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-fly': (s, el, opts) => {
    const area = safeNum(s.animationArea, 100);
    const dir = s.direction ?? 'up';
    const rad = directionToDegree(dir); // reuse your existing helper

    const dx = Math.cos(rad) * area;
    const dy = Math.sin(rad) * area;

    // skew (can be empty string or number in CMS)
    const skewVal = safeNum(Number(s.skew), 0);

    return gsap.fromTo(
      el,
      {
        x: dx,
        y: dy,
        opacity: s.startOpacity ?? 0,
        skewX: skewVal,
        skewY: skewVal,
      },
      {
        x: 0,
        y: 0,
        opacity: 1,
        skewX: 0,
        skewY: 0,
        ease: s.easing ?? 'power1.out',
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-turn': (s, el, opts) => {
    const area = safeNum(s.animationArea, 100);
    const scale = safeNum(s.scale, 1);
    const dir = s.direction ?? 'up';
    const rad = directionToDegree(dir);

    const dx = Math.cos(rad) * area;
    const dy = Math.sin(rad) * area;

    return gsap.fromTo(
      el,
      {
        x: dx,
        y: dy,
        opacity: 0,
        rotation: dir === 'left' || dir === 'right' ? -90 : 90,
        scale: 0.5,
      },
      {
        x: 0,
        y: 0,
        opacity: 1,
        rotation: 0,
        scale: scale,
        ease: s.easing ?? 'power2.out',
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-tilt': (s, el, opts) => {
    const area = safeNum(s.animationArea, 100);
    const speed = safeNum(s.speed, 1);
    const dir = String(s.direction || 'up').toLowerCase();

    // convert direction to radians (reusing your helper)
    const rad = directionToDegree(dir);

    const dx = Math.cos(rad) * area;
    const dy = Math.sin(rad) * area;

    // choose axis based tilt
    let rotateProp: 'rotateX' | 'rotateY';
    let tiltStart: number;

    if (dir === 'up' || dir === 'down') {
      rotateProp = 'rotateX';
      tiltStart = dir === 'up' ? 45 : -45;
    } else {
      rotateProp = 'rotateY';
      tiltStart = dir === 'left' ? -45 : 45;
    }

    return gsap.fromTo(
      el,
      {
        x: dx,
        y: dy,
        opacity: 0,
        [rotateProp]: tiltStart,
      },
      {
        x: 0,
        y: 0,
        opacity: 1,
        [rotateProp]: 0,
        ease: s.easing ?? 'power2.out',
        duration: speed,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-flip': (s, el, opts) => {
    const area = safeNum(s.animationArea, 100);
    const rotation = safeNum(s.rotation, 180); // default flip angle
    const orientation = String(s.orientation || 'horizontal').toLowerCase();

    // Axis selection
    const axis = orientation === 'vertical' ? 'rotationX' : 'rotationY';

    return gsap.fromTo(
      el,
      {
        [axis]: rotation,
        opacity: 0,
        y: orientation === 'vertical' ? area : 0,
        x: orientation === 'horizontal' ? area : 0,
        transformPerspective: 800, // makes 3D rotation visible
        transformOrigin: 'center center',
      },
      {
        [axis]: 0,
        opacity: 1,
        x: 0,
        y: 0,
        ease: s.easing ?? 'power2.out',
        duration: s.speed ?? 1.2,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-parallax': (s, el, opts) => {
    const speed = safeNum(s.speed, 0.5);
    const distance = safeNum(s.distance, 100);

    return gsap.to(el, {
      y: -distance,
      ease: 'none',
      ...opts,
      scrollTrigger: {
        trigger: el,
        start: s.start ?? 'top bottom',
        end: s.end ?? 'bottom top',
        scrub: speed,
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-shutters': (s, el, opts) => {
    // const parts = safeNum(s.number_of_parts, 4);
    const stagger = safeNum(s.stagger, 0.1);

    return gsap.fromTo(
      el,
      {
        opacity: 0,
        clipPath: 'inset(0 100% 0 0)',
      },
      {
        opacity: 1,
        clipPath: 'inset(0 0% 0 0)',
        stagger: stagger,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-stretch': (s, el, opts) => {
    const percentage = safeNum(s.stretch_percentage, 150);
    const orientation = s.orientation ?? 'horizontal';

    const stretchProp = orientation === 'vertical' ? 'scaleY' : 'scaleX';
    const scaleValue = percentage / 100;

    return gsap.fromTo(
      el,
      { [stretchProp]: 0.1, opacity: 0 },
      {
        [stretchProp]: scaleValue,
        opacity: 1,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-test': (s, el, opts) => {
    // Simple test animation - fade and move up
    return gsap.fromTo(
      el,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 20%',
          scrub: s.scrub ?? false,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? true, // Show markers for testing
        },
      }
    );
  },
};
